<template>
  <mask-variation
    v-if="mask"
    :style="inputRadius"
    v-bind="{...$attrs, ...commonProps, ...$props}"
    v-on="$listeners"
  >
    <slot v-for="(_, name) in $slots" :slot="name" :name="name" />
  </mask-variation>

  <currency-variation
    v-else-if="type === 'currency'"
    :style="inputRadius"
    v-bind="{...$attrs, ...commonProps, ...$props}"
    v-on="$listeners"
  >
    <slot v-for="(_, name) in $scopedSlots" :slot="name" :name="name" />
  </currency-variation>

  <password-variation
    v-else-if="type === 'password'"
    :style="inputRadius"
    v-bind="{...$attrs, ...commonProps, ...$props}"
    v-on="$listeners"
  >
    <slot v-for="(_, name) in $scopedSlots" :slot="name" :name="name" />
  </password-variation>

  <v-text-field
    v-else
    :style="inputRadius"
    v-bind="{...$attrs, ...commonProps, ...$props}"
    v-on="$listeners"
  >
    <!--Slot padrão do componente -->
    <slot v-for="(_, name) in $slots" :slot="name" :name="name" />
  </v-text-field>
</template>

<script>
import inputControlMixin from '../inputControlPropsMixin'

import MaskVariation from './Variations/MaskVariation.vue'
import CurrencyVariation from './Variations/CurrencyVaration.vue'
import PasswordVariation from './Variations/PasswordVariation.vue'

/**
 * O input é um dos principais componentes para receber
 * input do usuário nos formulários. Ele tem muitas
 * variações e cobre boa parte dos casos de tipo de dados.
 * @displayName Input
 */
export default {
  name: 'ZInput',
  components: {
    MaskVariation,
    CurrencyVariation,
    PasswordVariation,
  },
  mixins: [inputControlMixin],

  props: {
    /**
     * Tipo do input
     */
    type: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * Adiciona funcionalidade de "limpar" o input
     */
    clearable: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Máscara para ser aplicada, com formato (vue-the-mask)
     */
    mask: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * Número mínimo aceito pelo input
     */
    min: {
      type: Number,
      required: false,
      default: -Infinity,
    },
    /**
     * Número máximo aceito pelo input
     */
    max: {
      type: Number,
      required: false,
      default: Infinity,
    },
    /**
     * Flag para indicar que o input deve ser grande (defaults: {mobile: true, desktop: false})
     */
    large: {
      type: Boolean,
      required: false,
    },
    /**
     * Flag para indicar que o input deve ser pequeno (defaults: {mobile: false, desktop: false})
     */
    small: {
      type: Boolean,
      required: false,
    },
    /**
     * Flag para indicar que o input deve ser medio sempre (defaults: {mobile: false, desktop: true})
     */
    medium: {
      type: Boolean,
      default: false,
    },
    /**
     * Tamanho máximo da string
     */
    maxlength: {
      required: false,
      default: Infinity,
    },
    /**
     * Flag para colocar um contador de caracteres
     */
    counter: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Icone para fazer o append
     */
    icon: {
      type: String,
      required: false,
    },
    /**
     * Ícone para fazer prepend
     */
    prependIcon: {
      type: String,
      required: false,
    },
    /**
     * Prefixo do input
     */
    prefix: {
      type: String,
      default: '',
    },
    /**
     * Sufixo do input
     */
    suffix: {
      type: String,
      default: '',
    },
    /**
     * Prop a ser usada quando `type='currency'`
     *
     * @typedef String [BRL,EUR,USD]
     */
    currency: {
      type: String,
      default: 'BRL',
    },

    /**
     * Customização do locale ao usar o `type='currency'`
     */
    locale: {
      type: String,
      default: 'pt-BR',
    },

    /**
     * Prop para customizar o radius do input
     */
    radius: {
      type: Number,
      default: 0,
    },
  },

  computed: {
    commonProps() {
      return {
        outlined: true,
        persistentHint: true,
        class: `zoox-component zoox-input ${this.small ? 'small' : ''} ${
          this.isDense ? '' : 'large'
        }`,
        clearIcon: '$clear_rounded',
        hint: this.hint,
        dense: this.isDense,
        type: this.type,
        label: this.currentLabel,
        value: this.value,
        disabled: this.disabled,
        rules: this.rules,
        errorMessages: this.errorMessages,
        appendIcon: this.icon,
        prependInnerIcon: this.prependIcon,
        placeholder: this.placeholder,
        maxlength: this.maxlength,
        counter: this.counter,
        hideDetails: this.hideDetails,
        readonly: this.readonly,
        prefix: this.prefix,
        suffix: this.suffix,
        clearable: this.clearable,
      }
    },

    inputRadius() {
      if (this.radius) {
        return `border-radius: ${this.radius}px !important`
      }

      if (this.small) {
        return 'border-radius: var(--component-input-sm-radius) !important'
      }

      return 'border-radius: var(--component-input-md-radius) !important'
    },
  },

  methods: {
    onInput(value) {
      /**
       * Emitido quando alguma tecla é clicada
       *
       * @property {String} value - Valor do input
       */
      this.$emit('input', value)
    },
    onBlur(value) {
      /**
       * Emitido quando o input é deixado de ser focado
       *
       * @property {String} value - Valor do input
       */
      this.$emit('blur', value)
    },
    onChange(value) {
      /**
       * Emitido quando efetivamente muda o valor do modelo
       *
       * @property {String} value - Valor do input
       */
      this.$emit('change', value)
    },
  },
}
</script>

<style lang="scss">
@use '@scss/global' as g;

.zoox-input {
  border-radius: var(--component-input-md-radius) !important;

  input {
    padding-top: 12px;
  }

  &.large .v-label:not(.v-label--active) {
    top: 20px !important;
  }

  &.small {
    @include g.zoox-small-form-input;
  }

  &:not(.error--text) {
    .v-input__slot:hover {
      fieldset {
        color: var(--primary) !important;
      }
    }
  }
}
</style>

<style lang="scss">
.zoox-input {
  .v-label:not(.v-label--active) {
    top: 12px;
  }
}
</style>
